/**
 * Design token CSS variable names.
 * Authored SSOT: src/styles/tokens/{primitive,semantic,component}.css
 * Runtime entry: src/index.css (@imports authored tokens + global base styles).
 * Ant Design bridge: createAntdThemeFromCssVars() in tenant/business/frontend/config/antdTheme.ts
 *
 * @see docs/inflomatrix-constitution/UI_TOKEN_MAPPING.md
 */

/** Tier 1 — Primitive */
export const PRIMITIVE_CSS_VARS = {
  colorPrimary500: '--color-primary-500',
  colorSuccess500: '--color-success-500',
  colorWarning500: '--color-warning-500',
  colorError500: '--color-error-500',
  colorNeutral0: '--color-neutral-0',
  colorNeutral50: '--color-neutral-50',
  colorNeutral100: '--color-neutral-100',
  colorNeutral900: '--color-neutral-900',
  space3: '--space-3',
  space4: '--space-4',
  space6: '--space-6',
  radiusSm: '--radius-sm',
  radiusMd: '--radius-md',
  radiusLg: '--radius-lg',
  fontSizeBase: '--font-size-base',
  fontSizeSm: '--font-size-sm',
  fontWeightMedium: '--font-weight-medium',
  lineHeightNormal: '--line-height-normal',
} as const;

/** Tier 2 — Semantic */
export const SEMANTIC_CSS_VARS = {
  primaryColor: '--primary-color',
  surfaceCanvas: '--surface-canvas',
  surfaceSubtle: '--surface-subtle',
  textPrimary: '--text-primary',
  textMuted: '--text-muted',
  borderDefault: '--border-default',
  borderSubtle: '--border-subtle',
} as const;

/** Tier 3 — Component */
export const COMPONENT_CSS_VARS = {
  buttonHeightMd: '--button-height-md',
  buttonHeightSm: '--button-height-sm',
  buttonHeightLg: '--button-height-lg',
  formInputHeight: '--form-input-height',
  tableHeaderBg: '--table-header-bg',
  tableFontSize: '--table-font-size',
  cardBorderRadius: '--card-border-radius',
  cardPadding: '--card-padding',
  badgeSuccessColor: '--badge-success-color',
  badgeErrorColor: '--badge-error-color',
} as const;

/**
 * Ant Design ThemeConfig.token field → CSS custom property (read at runtime).
 * Values resolved via getComputedStyle in createAntdThemeFromCssVars().
 */
export const ANT_DESIGN_TOKEN_CSS_VAR_MAP = {
  colorPrimary: PRIMITIVE_CSS_VARS.colorPrimary500,
  colorSuccess: PRIMITIVE_CSS_VARS.colorSuccess500,
  colorWarning: PRIMITIVE_CSS_VARS.colorWarning500,
  colorError: PRIMITIVE_CSS_VARS.colorError500,
  borderRadius: PRIMITIVE_CSS_VARS.radiusSm,
  colorBgLayout: SEMANTIC_CSS_VARS.surfaceSubtle,
  colorText: SEMANTIC_CSS_VARS.textPrimary,
  colorTextSecondary: SEMANTIC_CSS_VARS.textMuted,
  colorBorder: SEMANTIC_CSS_VARS.borderDefault,
  colorBgContainer: SEMANTIC_CSS_VARS.surfaceCanvas,
  controlHeight: COMPONENT_CSS_VARS.buttonHeightMd,
  controlHeightSM: COMPONENT_CSS_VARS.buttonHeightSm,
  controlHeightLG: COMPONENT_CSS_VARS.buttonHeightLg,
  fontSize: PRIMITIVE_CSS_VARS.fontSizeBase,
  fontSizeSM: PRIMITIVE_CSS_VARS.fontSizeSm,
  lineHeight: PRIMITIVE_CSS_VARS.lineHeightNormal,
  borderRadiusLG: PRIMITIVE_CSS_VARS.radiusLg,
  motionDurationMid: '--motion-duration-normal',
} as const;

export type AntDesignTokenKey = keyof typeof ANT_DESIGN_TOKEN_CSS_VAR_MAP;
